import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import StatusBadge from "../components/StatusBadge";
import Modal from "../components/Modal";

const CATEGORY_LABEL = {
  ski: "Ski", boots: "Schuhe", poles: "Stöcke", binding: "Bindungen", lurk: "Lurk",
};
const CAT_ORDER = ["ski", "boots", "poles", "binding", "lurk"];

export default function QrCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("");
  const [zoomItem, setZoomItem] = useState(null);

  useEffect(() => {
    api.listItems().then(setItems).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (activeCat && i.equipment?.category !== activeCat) return false;
      if (i.status === "retired") return false;
      if (!q) return true;
      const eq = i.equipment;
      const hay = [i.id, eq?.brand, eq?.model, eq?.size, i.serial_number]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, activeCat]);

  const grouped = useMemo(() => {
    const g = {};
    for (const it of filtered) {
      const c = it.equipment?.category || "other";
      (g[c] ||= []).push(it);
    }
    for (const k of Object.keys(g)) g[k].sort((a, b) => a.id.localeCompare(b.id));
    return g;
  }, [filtered]);

  const counts = useMemo(() => {
    const c = {};
    for (const it of items) {
      if (it.status === "retired") continue;
      c[it.equipment?.category] = (c[it.equipment?.category] || 0) + 1;
    }
    return c;
  }, [items]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl">QR-Katalog</h1>
          <p className="text-fh-gray text-sm mt-1">
            {filtered.length} von {items.length} Items · klick auf einen QR um ihn groß anzuzeigen oder zu drucken
          </p>
        </div>
        <a href={api.exportQrCatalog()} className="btn-primary" download>
          📄 Komplett-PDF
        </a>
      </div>

      <div className="card p-4 space-y-3">
        <input
          className="input-field"
          type="search"
          placeholder="Suche nach ID, Marke, Modell, Größe…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCat("")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeCat === "" ? "bg-fh-black text-white" : "bg-white border border-gray-300 hover:bg-fh-gray-light"}`}
          >Alle ({items.filter((i) => i.status !== "retired").length})</button>
          {CAT_ORDER.filter((c) => counts[c]).map((c) => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeCat === c ? "bg-fh-black text-white" : "bg-white border border-gray-300 hover:bg-fh-gray-light"}`}
            >
              {CATEGORY_LABEL[c]} ({counts[c]})
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="card p-8 text-center text-fh-gray">Lade Inventar…</div>
      ) : (
        CAT_ORDER.filter((c) => grouped[c]?.length).map((cat) => (
          <section key={cat} className="card overflow-hidden">
            <div className="px-5 py-3 bg-fh-black text-white">
              <h2 className="font-semibold text-sm uppercase tracking-wider">
                {CATEGORY_LABEL[cat]} · {grouped[cat].length}
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-4">
              {grouped[cat].map((it) => (
                <button
                  key={it.id}
                  onClick={() => setZoomItem(it)}
                  className="bg-white border border-gray-200 rounded-md p-3 hover:shadow-card hover:border-fh-red transition-all text-left flex flex-col items-center gap-2"
                >
                  <img
                    src={api.itemQrUrl(it.id)}
                    alt={`QR ${it.id}`}
                    className="w-full aspect-square bg-fh-gray-light rounded object-contain"
                    loading="lazy"
                  />
                  <div className="text-center w-full min-w-0">
                    <div className="font-mono text-sm truncate">{it.id}</div>
                    <div className="text-xs text-fh-gray truncate">
                      {[it.equipment?.brand, it.equipment?.model, it.equipment?.size]
                        .filter(Boolean).join(" ")}
                    </div>
                    <div className="mt-1"><StatusBadge status={it.status} /></div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        ))
      )}

      {zoomItem && (
        <Modal
          open
          title={zoomItem.id}
          onClose={() => setZoomItem(null)}
          size="md"
          footer={
            <>
              <button onClick={() => setZoomItem(null)} className="btn-secondary">Schließen</button>
              <a href={api.itemQrUrl(zoomItem.id)} download={`${zoomItem.id}.png`} className="btn-primary">
                ⬇️ PNG
              </a>
              <Link to={`/items/${zoomItem.id}`} className="btn-ghost">Zum Item →</Link>
            </>
          }
        >
          <div className="space-y-3 text-center">
            <img
              src={api.itemQrUrl(zoomItem.id)}
              alt={zoomItem.id}
              className="mx-auto bg-white max-w-xs rounded"
            />
            <div className="text-fh-gray text-sm">
              {[zoomItem.equipment?.brand, zoomItem.equipment?.model, zoomItem.equipment?.size]
                .filter(Boolean).join(" ")}
            </div>
            <button
              onClick={() => {
                const w = window.open("", "_blank");
                if (w) {
                  w.document.write(`
                    <html><head><title>QR ${zoomItem.id}</title></head>
                    <body style="margin:0;padding:40px;text-align:center;font-family:sans-serif">
                      <img src="${api.itemQrUrl(zoomItem.id)}" style="max-width:300px"/>
                      <h2 style="font-family:monospace">${zoomItem.id}</h2>
                      <script>window.print();</script>
                    </body></html>
                  `);
                  w.document.close();
                }
              }}
              className="btn-secondary"
            >🖨️ Drucken</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
